/**
 * EpicFeaturesPanel - Reusable panel for Epic Features display
 * 
 * Used in:
 * - EpicDetailModal (edit mode)
 * - EpicMergeModal (source view + merged form)
 * 
 * Props:
 * - features: array of feature objects linked to this epic
 * - isLoading: boolean
 * - colors: theme colors from useSafeMode
 * - onSelectFeature: (feature) => void (opens feature detail modal)
 * - mode: 'edit' | 'create' | 'view'
 */

import React from 'react';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { cn } from '../../lib/utils';
import { Layers, CheckCircle2, Loader2, Eye, Circle } from 'lucide-react';

// Feature status badge colors
const FEATURE_STATUS_CONFIG = {
  'Done': { color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400', icon: CheckCircle2 },
  'In Progress': { color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400', icon: Loader2 },
  'Review': { color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400', icon: Eye },
  'Backlog': { color: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400', icon: Circle },
};

const EpicFeaturesPanel = ({
  features = [],
  isLoading = false,
  colors,
  onSelectFeature,
  mode = 'edit',
}) => {
  // Calculate progress stats
  const progressStats = features.reduce((acc, f) => {
    acc.total++;
    if (f.status === 'Done') acc.done++;
    return acc;
  }, { total: 0, done: 0 });

  const progressPercent = progressStats.total > 0 
    ? Math.round((progressStats.done / progressStats.total) * 100) 
    : 0;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Label className={cn("flex items-center gap-2", colors.textPrimary)}>
          <Layers className="w-4 h-4" />
          Linked Features ({features.length})
        </Label>
        {features.length > 0 && (
          <span className={cn("text-sm", colors.textMuted)}>
            {progressStats.done}/{progressStats.total} complete ({progressPercent}%)
          </span>
        )}
      </div>

      {/* Loading State */}
      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className={cn("w-6 h-6 animate-spin", colors.textMuted)} />
        </div>
      ) : features.length === 0 ? (
        /* Empty State */
        <div className={cn("text-center py-8 rounded-lg border", colors.bgTertiary, colors.borderPrimary)}>
          <Layers className={cn("w-8 h-8 mx-auto mb-2 opacity-50", colors.textMuted)} />
          <p className={cn("text-sm", colors.textMuted)}>No features linked to this epic yet.</p>
          {mode === 'create' && (
            <p className={cn("text-xs mt-1", colors.textMuted)}>
              Features will be assigned after the merge is complete
            </p>
          )}
        </div>
      ) : (
        /* Features List */
        <div className={cn("rounded-lg border divide-y", colors.borderPrimary, "divide-gray-200 dark:divide-gray-700")}>
          {features.map((feature) => {
            const statusConfig = FEATURE_STATUS_CONFIG[feature.status] || FEATURE_STATUS_CONFIG['Backlog'];
            const StatusIcon = statusConfig.icon;
            
            return (
              <div
                key={feature.feature_id}
                className={cn(
                  "px-4 py-3 flex items-center justify-between transition-colors",
                  onSelectFeature && "hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer",
                  colors.textPrimary
                )}
                onClick={() => onSelectFeature?.(feature)}
                data-testid={`linked-feature-${feature.feature_id}`}
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <StatusIcon className={cn(
                    "w-5 h-5 flex-shrink-0",
                    feature.status === 'Done' ? 'text-green-500' :
                    feature.status === 'In Progress' ? 'text-blue-500' :
                    feature.status === 'Review' ? 'text-purple-500' :
                    colors.textMuted
                  )} />
                  <div className="flex-1 min-w-0">
                    <span className="font-medium truncate">{feature.title}</span>
                    {feature.requirements?.length > 0 && (
                      <span className={cn("ml-2 text-xs", colors.textMuted)}>
                        {feature.requirements.length} requirements
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Badge variant="outline" className={statusConfig.color}>
                    {feature.status}
                  </Badge>
                  {feature.effort_size && (
                    <span className={cn("text-xs px-2 py-0.5 rounded", colors.bgTertiary, colors.textMuted)}>
                      {feature.effort_size}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Progress Bar - Only show when there are features */}
      {features.length > 0 && (
        <div className={cn("p-3 rounded-lg border", colors.bgTertiary, colors.borderPrimary)}>
          <div className="flex items-center justify-between text-sm mb-2">
            <span className={colors.textSecondary}>Feature Completion</span>
            <span className={cn("font-medium", colors.textPrimary)}>
              {progressPercent}%
            </span>
          </div>
          <div className={cn("w-full h-2 rounded-full overflow-hidden", "bg-gray-200 dark:bg-gray-700")}>
            <div 
              className="h-full bg-gradient-to-r from-green-500 to-green-600 rounded-full transition-all"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default EpicFeaturesPanel;
